import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { sortSalesForListing } from '../utils/currentSale';
import { format } from 'date-fns';

const AuctionList: React.FC = () => {
  const { userProfile } = useAuth();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadAuctions();
  }, [userProfile]);

  const loadAuctions = async () => {
    if (!userProfile) return;

    try {
      // 🔴 The orderBy('createdAt','desc') that used to be on this query is gone.
      // Firestore does not sort documents missing the ordered field to the end —
      // it OMITS them, so any sale without a createdAt was absent from this list
      // entirely, with no empty row and no count to reveal it. On the pages a
      // seller uses to see what sales exist, that is the worst place for it.
      //
      // The where() filter stays (it is what scopes the list to this auction
      // house). Ordering moves in-memory onto a key that is actually present:
      // the real end of the sale. As a side effect the query no longer needs a
      // composite index.
      const auctionsRef = collection(db, 'auctions');
      const q = query(
        auctionsRef,
        where('auctionHouseId', '==', userProfile.auctionHouseId),
      );

      const snapshot = await getDocs(q);
      const auctionsList: Auction[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate(),
          endDate: data.endDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Auction;
      });

      // Newest real end first, drafts included — a list must never drop a sale,
      // only order it. Shared with SalePicker so all three agree.
      setAuctions(sortSalesForListing(auctionsList));
    } catch (error) {
      console.error('Error loading auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAuctions = filterStatus === 'all'
    ? auctions
    : auctions.filter(a => a.status === filterStatus);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Auctions</h1>
        <Link to="/import" className="btn-primary">
          Import New Auction
        </Link>
      </div>

      {/* Filter */}
      <div className="card">
        <div className="flex items-center gap-4">
          <span className="font-semibold">Filter:</span>
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              filterStatus === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({auctions.length})
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              filterStatus === 'draft'
                ? 'bg-gray-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Draft ({auctions.filter(a => a.status === 'draft').length})
          </button>
          <button
            onClick={() => setFilterStatus('scheduled')}
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              filterStatus === 'scheduled'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Scheduled ({auctions.filter(a => a.status === 'scheduled').length})
          </button>
          <button
            onClick={() => setFilterStatus('live')}
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              filterStatus === 'live'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Live ({auctions.filter(a => a.status === 'live').length})
          </button>
        </div>
      </div>

      {/* Auction List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading auctions...</p>
        </div>
      ) : filteredAuctions.length === 0 ? (
        <div className="text-center py-12 card">
          <p className="text-gray-600 mb-4">
            {filterStatus === 'all' ? 'No auctions yet' : `No ${filterStatus} auctions`}
          </p>
          <Link to="/import" className="btn-primary">
            Import Your First Auction
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {filteredAuctions.map((auction) => (
            <Link
              key={auction.id}
              to={`/auctions/${auction.id}`}
              className="card hover:shadow-lg transition"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{auction.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {format(auction.startDate, 'MMM dd, yyyy h:mm a')}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    auction.status === 'live'
                      ? 'bg-green-100 text-green-800'
                      : auction.status === 'scheduled'
                      ? 'bg-blue-100 text-blue-800'
                      : auction.status === 'draft'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}
                >
                  {auction.status}
                </span>
              </div>

              {auction.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {auction.description}
                </p>
              )}

              <div className="grid grid-cols-3 gap-4 text-center border-t pt-4">
                <div>
                  <p className="text-2xl font-bold text-primary-600">
                    {auction.totalLots}
                  </p>
                  <p className="text-xs text-gray-600">Lots</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-600">
                    {auction.syncStatus === 'synced' ? '✓' : '○'}
                  </p>
                  <p className="text-xs text-gray-600">AuctionRinger</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-600">
                    {auction.location?.substring(0, 3) || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-600">Location</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuctionList;